import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface TicketDashboardSummary {
  pending: number;
  inProgress: number;
  resolved: number;
}

export interface TicketBuilding {
  buildingId: number;
  buildingName: string;
  pending: number;
  inProgress: number;
  resolved: number;
  thisMonth?: { pending: number; inProgress: number; resolved: number };
}

export interface TicketCampus {
  campusId: number;
  campusName: string;
  pending: number;
  inProgress: number;
  resolved: number;
  buildings: TicketBuilding[];
}

export interface TicketDashboardResponse {
  summary: TicketDashboardSummary;
  campuses: TicketCampus[];
}

// Flat ticket response for /ticket/getticketdashboardforweb
export interface TicketWeb {
  ID: number;
  CATEGORY_ID: number;
  BUILDING_ID: number;
  ITEM_ID?: number;
  BLOCK_ID: number;
  FLOOR_ID: number;
  BUILDING_NAME: string;
  CAMPUS_ID: number;
  CAMPUS_NAME?: string;
  BLOCK_NAME: string;
  FLOOR_NUMBER: string;
  ROOM_NO: string;
  ROOM_ID: number;
  CATEGORY_NAME: string;
  ITEM_NAME: string;
  PRIORITY: string;
  ASSIGNED_TO_PAYROLL_ID: string;
  ASSIGNED_TO: string;
  USER_NAME: string;
  TICKET_MODE?: string;
  REMARKS: string;
  COMMENTS?: string;
  /**
   * Backend sends this as an array of URLs (e.g. [])
   * but older responses might still be a comma‑separated string,
   * so we accept both.
   */
  TICKET_IMAGE_PATH: string[] | string;
  CREATED_ON: string;
  STATUS: string;
  CREATED_BY?: string;
  CREATED_BY_DESIGNATION?: string;
  CREATED_BY_MOBILE?: string;
  REASON?: string;
  FLOOR_NAME?: string;
  LATITUDE?: string | number | null;
  LONGITUDE?: string | number | null;
}

@Injectable({
  providedIn: 'root'
})
export class TicketingService {

  /** Ticket API base URL from environment (e.g. http://192.168.20.83:8083) */
  readonly ticketUrl = environment.ticketUrl;

  private readonly dashboardUrl = `${this.ticketUrl}/ticket/getticketdashboardforweb`;
  private readonly statesUrl = `${this.ticketUrl}/MasterData/getStates`;
  private readonly zonesByStateUrl = `${this.ticketUrl}/MasterData/getZoneNamesByStateId`;
  private readonly citiesByZoneUrl = `${this.ticketUrl}/MasterData/getCityByZone`;
  private readonly campusesByCityUrl = `${this.ticketUrl}/MasterData/getCampusByCity`;
  private readonly buildingsByCampusUrl = `${this.ticketUrl}/MasterData/getBuildings`;
  private readonly ticketsByFilterUrl = `${this.ticketUrl}/ticket/getTicketsByFilter`;
  private readonly ticketImagesUrl = `${this.ticketUrl}/ticket/getTicketImage`;

  private get headers(): HttpHeaders {
    const token = this.authToken;
    const authHeader = token?.toLowerCase().startsWith('bearer ') ? token : `Bearer ${token}`;
    return new HttpHeaders({
      'Authorization': authHeader,
      'Content-Type': 'application/json'
    });
  }

  /** Use same token source as HttpService - update if your app uses auth service */
  private get authToken(): string {
    return sessionStorage.getItem('jwtToken')
      || localStorage.getItem('jwtToken')
      || ''
  }

  constructor(private http: HttpClient) { }

  getTicketDashBoardDetails(): Observable<TicketWeb[]> {
    return this.http.get<TicketWeb[]>(this.dashboardUrl, { headers: this.headers });
  }

  getStates(): Observable<unknown> {
    return this.http.get(this.statesUrl, { headers: this.headers });
  }

  getZonesByStateId(stateId: string | number): Observable<unknown> {
    return this.http.get(`${this.zonesByStateUrl}?stateId=${stateId}`, { headers: this.headers });
  }

  getCitiesByZone(stateId: string | number, zoneId: string | number): Observable<unknown> {
    return this.http.get(`${this.citiesByZoneUrl}?stateId=${stateId}&zoneId=${zoneId}`, { headers: this.headers });
  }

  getCampusesByCity(cityId: string | number): Observable<unknown> {
    return this.http.get(`${this.campusesByCityUrl}?cityId=${cityId}`, { headers: this.headers });
  }

  getBuildingsByCampus(campusId: string | number): Observable<unknown> {
    return this.http.get(`${this.buildingsByCampusUrl}?campusId=${campusId}`, { headers: this.headers });
  }

  getTicketsByFilter(params: {
    status: 'pending' | 'inprogress' | 'resolved';
    campusId: string | number;
    buildingId: string | number;
    type: 'today' | 'yesterday' | 'last7days' | 'thismonth';
  }): Observable<unknown> {
    const qs = new URLSearchParams({
      status: params.status,
      campusId: String(params.campusId),
      buildingId: String(params.buildingId),
      type: params.type
    }).toString();
    return this.http.get(`${this.ticketsByFilterUrl}?${qs}`, { headers: this.headers });
  }

  getTicketDashBoardDetailsWithFilters(params?: {
    stateId?: string;
    zoneId?: string;
    cityId?: string;
    campusId?: string;
    buildingId?: string;
  }): Observable<TicketWeb[]> {
    const apiParams: Record<string, string> = {};
    if (params?.stateId) apiParams['stateId'] = params.stateId;
    if (params?.zoneId) apiParams['zoneId'] = params.zoneId;
    if (params?.cityId) apiParams['cityId'] = params.cityId;
    if (params?.campusId) apiParams['campusId'] = params.campusId;
    if (params?.buildingId) apiParams['buildingId'] = params.buildingId;
    const qs = Object.keys(apiParams).length ? '?' + new URLSearchParams(apiParams).toString() : '';
    return this.http.get<TicketWeb[]>(`${this.dashboardUrl}${qs}`, { headers: this.headers });
  }

  getTicketImages(): Observable<Array<{ id: number; campusId: number; buildingId: number; ticketImg: string }>> {
    return this.http.get<Array<{ id: number; campusId: number; buildingId: number; ticketImg: string }>>(
      this.ticketImagesUrl,
      { headers: this.headers }
    );
  }
}

